import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuthUser } from "../hooks/AuthProvider";
import { getTenantsByUid } from "../helpers/firestoreService";

export const ROLE_PM = "pm";
export const ROLE_TENANT = "tenant";

export const ROLE_HOMES = {
  [ROLE_PM]: "/overview",
  [ROLE_TENANT]: "/tenant/home",
};

export const ROLE_LABELS = {
  [ROLE_PM]: "Property Manager",
  [ROLE_TENANT]: "Tenant",
};

export const ROLE_ICONS = {
  [ROLE_PM]: "bx bx-building",
  [ROLE_TENANT]: "bx bx-user",
};

const RoleContext = createContext(null);

export function useRoleContext() {
  return useContext(RoleContext);
}

export function RoleProvider({ children }) {
  const user = useAuthUser();

  const [availableRoles, setAvailableRoles] = useState([]);
  const [activeRole, setActiveRoleState] = useState(null);
  const [tenantRecords, setTenantRecords] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  // Bumped on every refreshRoles() call so hooks like useMyRentals (which may
  // be mounted on a page that didn't trigger the invitation accept, e.g. the
  // notification bell on TenantHome) know to refetch instead of showing stale data.
  const [rentalsVersion, setRentalsVersion] = useState(0);

  const resolveRoles = async (uid) => {
    setRolesLoading(true);
    try {
      const tenants = await getTenantsByUid(uid);
      setTenantRecords(tenants);

      const roles = [ROLE_PM, ROLE_TENANT];

      setAvailableRoles(roles);

      // Restore the last chosen role from localStorage; fall back to ROLE_TENANT.
      const stored = localStorage.getItem("ezron.activeRole");
      setActiveRoleState((prev) => {
        if (prev && roles.includes(prev)) return prev; // already set this session
        if (stored && roles.includes(stored)) return stored; // persisted from last session
        return ROLE_TENANT;
      });
    } catch (e) {
      console.error("Role resolution failed:", e);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    if (user === undefined) return; // Firebase auth still initializing
    if (!user) {
      setAvailableRoles([]);
      setActiveRoleState(null);
      localStorage.removeItem("ezron.activeRole");
      setTenantRecords([]);
      setRolesLoading(false);
      return;
    }
    resolveRoles(user.uid);
  }, [user]);

  /** Switch the active role and persist to localStorage. */
  const setActiveRole = (role) => {
    localStorage.setItem("ezron.activeRole", role);
    setActiveRoleState(role);
  };

  /**
   * Re-query Firestore for this user's tenant records.
   * Call after accepting an invitation so the new role appears in the switcher.
   */
  const refreshRoles = async () => {
    if (!user) return;
    await resolveRoles(user.uid);
    setRentalsVersion((v) => v + 1);
  };

  const value = {
    availableRoles,
    activeRole,
    setActiveRole,
    tenantRecords,
    rentalsVersion,
    // Prefer a record that's actually linked to a property — if this uid has
    // more than one tenant doc (e.g. a stale one from before being removed
    // and re-added), tenantRecords[0] isn't reliably the "real" current one.
    activeTenant: tenantRecords.find((t) => t.propertyId) || tenantRecords[0] || null,
    rolesLoading,
    refreshRoles,
    ROLE_PM,
    ROLE_TENANT,
    ROLE_HOMES,
    ROLE_LABELS,
    ROLE_ICONS,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}
